// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { consoleService } from '../../services/consoleService';
import { LeaderboardRecord } from '../../types/leaderboards';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import JsonEditor from '../JsonEditor/JsonEditor';
import ComingSoon from '../ComingSoon/ComingSoon';
import QuizManagement from '../QuizManagement/QuizManagement';
import './QuizSettings.scss';

const QuizSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [quizSettings, setQuizSettings] = useState<string>('{}');
  const [jsonError, setJsonError] = useState<string | null>(null);
  
  // Leaderboard state
  const [leaderboardRecords, setLeaderboardRecords] = useState<LeaderboardRecord[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'settings' | 'management' | 'leaderboard'>('settings');
  
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();
  const QUIZ_LEADERBOARD_ID = 'total_earned_coins_quiz';

  useEffect(() => {
    loadQuizSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'leaderboard') {
      loadLeaderboardData();
    }
  }, [activeTab]);

  const loadLeaderboardData = async () => {
    try {
      setLeaderboardLoading(true);
      const data = await consoleService.getQuizLeaderboard(QUIZ_LEADERBOARD_ID);
      setLeaderboardRecords(data.records || []);
    } catch (err) {
      console.error('Failed to load quiz leaderboard:', err);
      setLeaderboardRecords([]);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const handleDeleteRecord = (record: LeaderboardRecord, index: number) => {
    openConfirmDialog(
      'Remove Record',
      `Are you sure you want to remove the record for "${record.username || record.owner_id}"?`,
      async () => {
        try {
          await consoleService.deleteLeaderboardRecord('', QUIZ_LEADERBOARD_ID, record.owner_id);
          setLeaderboardRecords(prev => prev.filter((_, i) => i !== index));
          setSuccessMessage('Record removed successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to remove record');
        }
      }
    );
  };

  const handleResetLeaderboard = () => {
    openConfirmDialog(
      'Reset Leaderboard',
      'Are you sure you want to reset the Quiz leaderboard? This will delete all records.',
      async () => {
        setResetting(true);
        try {
          await consoleService.leaderboardReset(QUIZ_LEADERBOARD_ID);
          await loadLeaderboardData();
          setSuccessMessage('Leaderboard reset successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to reset leaderboard');
        } finally {
          setResetting(false);
        }
      }
    );
  };

  const formatScore = (score: string): string => {
    return parseInt(score).toLocaleString();
  };

  const getRankIcon = (rank: number): string => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '🏅';
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const loadQuizSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      // Load quiz settings from backend or use default
      const settings = getDefaultQuizSettings();
      setQuizSettings(JSON.stringify(settings, null, 2));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Quiz settings');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultQuizSettings = () => {
    return {
      gameRules: {
        questionsPerGame: 10,
        timePerQuestion: 30,
        totalGameTime: 600,
        enableHints: true,
        enableSkip: true,
        maxSkips: 2
      },
      categories: {
        available: ["general", "science", "history", "sports", "entertainment", "technology", "geography"],
        default: "general",
        customCategories: true,
        categoryWeights: {
          general: 0.3,
          science: 0.2,
          history: 0.15,
          sports: 0.15,
          entertainment: 0.1,
          technology: 0.05,
          geography: 0.05
        }
      },
      difficulty: {
        levels: ["easy", "medium", "hard"],
        distribution: {
          easy: 0.4,
          medium: 0.4,
          hard: 0.2
        },
        adaptiveDifficulty: true,
        difficultyProgression: true
      },
      scoring: {
        correctAnswer: 10,
        timeBonus: 5,
        streakBonus: {
          3: 5,
          5: 15,
          10: 30
        },
        difficultyMultiplier: {
          easy: 1.0,
          medium: 1.5,
          hard: 2.0
        }
      },
      coinSystem: {
        entryFee: 5,
        winnerReward: 100,
        participationReward: 10,
        correctAnswerReward: 2,
        perfectGameBonus: 50,
        streakRewards: {
          5: 10,
          10: 25,
          20: 50
        }
      },
      lifelines: {
        fiftyFifty: {
          enabled: true,
          cost: 10,
          limit: 1,
          cooldown: 0
        },
        skipQuestion: {
          enabled: true,
          cost: 15,
          limit: 2,
          cooldown: 0
        },
        extraTime: {
          enabled: true,
          cost: 5,
          limit: 3,
          additionalTime: 15
        },
        hint: {
          enabled: true,
          cost: 8,
          limit: 2,
          cooldown: 0
        }
      },
      powerUps: {
        enabled: true,
        doublePoints: {
          cost: 20,
          duration: 5,
          multiplier: 2.0
        },
        freezeTime: {
          cost: 25,
          duration: 10,
          uses: 1
        },
        revealAnswer: {
          cost: 30,
          uses: 1,
          cooldown: 5
        }
      },
      matchmaking: {
        enableSinglePlayer: true,
        enableMultiplayer: true,
        enableTournaments: true,
        maxPlayersPerGame: 8,
        matchmakingTimeout: 30,
        skillBasedMatching: true
      },
      achievements: {
        firstCorrectAnswer: 50,
        perfectGame: 200,
        categoryMaster: 100,
        speedDemon: 150,
        knowledgeSeeker: 75,
        streakMaster: 300
      }
    };
  };

  const handleSaveQuizSettings = async () => {
    try {
      setLoading(true);
      const parsedSettings = JSON.parse(quizSettings);
      // Save to backend
      await consoleService.updateGameSettings('quiz', parsedSettings);
      setHasUnsavedChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save Quiz settings');
    } finally {
      setLoading(false);
    }
  };

  const handleQuizSettingsChange = (value: string) => {
    setQuizSettings(value);
    setHasUnsavedChanges(true);
    
    // Validate JSON
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'Invalid JSON format');
    }
  };

  const resetQuizSettings = () => {
    const defaultSettings = getDefaultQuizSettings();
    setQuizSettings(JSON.stringify(defaultSettings, null, 2));
    setJsonError(null);
    setHasUnsavedChanges(true);
  };

  const validateQuizSettings = () => {
    try {
      const parsed = JSON.parse(quizSettings);
      
      // Basic validation for required sections
      const requiredSections = ['gameRules', 'categories', 'difficulty', 'scoring', 'lifelines'];
      const missingSections = requiredSections.filter(section => !parsed[section]);
      
      if (missingSections.length > 0) {
        setJsonError(`Missing required sections: ${missingSections.join(', ')}`);
        return false;
      }
      
      setJsonError(null);
      return true;
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'Invalid JSON format');
      return false;
    }
  };

  if (loading) {
    return (
      <div className="quiz-settings">
        <div className="loading-spinner">Loading Quiz settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-settings">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadQuizSettings} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-settings">
      <div className="quiz-settings-header">
        <h1>🧠 Quiz Game Settings</h1>
        <p>Configure Quiz game questions, categories, lifelines, and scoring system</p>
        
        {hasUnsavedChanges && (
          <div className="unsaved-changes-banner">
            <span>⚠️ You have unsaved changes</span>
            <div className="save-buttons">
              <button onClick={handleSaveQuizSettings} className="btn btn-primary" disabled={loading || !!jsonError}>
                {loading ? 'Saving...' : 'Save Game Settings'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <span className="tab-icon">🧠</span>
          <span className="tab-text">Game Settings</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'management' ? 'active' : ''}`}
          onClick={() => setActiveTab('management')}
        >
          <span className="tab-icon">❓</span>
          <span className="tab-text">Question Management</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          <span className="tab-icon">🏆</span>
          <span className="tab-text">Leaderboard</span>
        </button>
      </div>

      <div className="quiz-settings-content">
        {/* Game Settings Tab */}
        {activeTab === 'settings' && (
          <div className="coming-soon-container">
            <ComingSoon 
              title="Quiz Game Settings"
              subtitle="Advanced Configuration System"
              description="We're developing a comprehensive configuration system for Quiz games with category management, difficulty settings, lifeline controls, and scoring customization."
              showCountdown={false}
              showNotifyButton={false}
            />
          </div>
        )}

        {/* Question Management Tab */}
        {activeTab === 'management' && (
          <div className="quiz-management-tab">
            <QuizManagement />
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="leaderboard-tab">
            <div className="leaderboard-header">
              <h3>🏆 Quiz Leaderboard</h3>
              <div className="leaderboard-actions">
                <button
                  className="btn btn-danger"
                  onClick={handleResetLeaderboard}
                  disabled={resetting}
                >
                  {resetting ? 'Resetting...' : '🔄 Reset Leaderboard'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={loadLeaderboardData}
                  disabled={leaderboardLoading}
                >
                  {leaderboardLoading ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
            </div>

            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            {leaderboardLoading ? (
              <div className="loading-spinner">Loading leaderboard...</div>
            ) : leaderboardRecords.length > 0 ? (
              <div className="leaderboard-table">
                <div className="table-header">
                  <div className="header-cell rank-cell">Rank</div>
                  <div className="header-cell player-cell">Player</div>
                  <div className="header-cell score-cell">Score</div>
                  <div className="header-cell date-cell">Last Updated</div>
                  <div className="header-cell actions-cell">Actions</div>
                </div>
                <div className="table-body">
                  {leaderboardRecords.map((record, index) => {
                    const rank = record.rank || (index + 1);
                    return (
                      <div key={record.owner_id} className={`table-row ${rank <= 3 ? 'top-rank' : ''}`}>
                        <div className="table-cell rank-cell">
                          <span className="rank-icon">{getRankIcon(rank)}</span>
                          <span className="rank-number">#{rank}</span>
                        </div>
                        <div className="table-cell player-cell">
                          <div className="player-info">
                            <div className="player-name">{record.username || 'Anonymous'}</div>
                            <div className="player-id">ID: {record.owner_id.substring(0, 8)}...</div>
                          </div>
                        </div>
                        <div className="table-cell score-cell">
                          <div className="score-value">{formatScore(record.score)}</div>
                        </div>
                        <div className="table-cell date-cell">
                          <div className="update-time">{formatDate(record.update_time)}</div>
                        </div>
                        <div className="table-cell actions-cell">
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteRecord(record, index)}
                          >
                            🗑️ Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="empty-leaderboard">
                <div className="empty-icon">🏆</div>
                <h3>No Rankings Yet</h3>
                <p>No players have earned coins in Quiz yet.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default QuizSettings;
